import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {Machine} from 'src/app/model/machine';
import {MapService} from 'src/app/services/map.service';
import * as mapboxgl from 'mapbox-gl';
import {TranslocoService} from '@ngneat/transloco';

import {AuthGuard} from "../../../../common/auth-guard.service";
import {MachineDetailsService} from 'src/app/services/machine-details.service';
import {interval, Subscription} from 'rxjs';
import {AlarmTelemetryData} from 'src/app/model/alarm-telemetry-data';
import {MachinesService} from "../../../../services/machines.service";
import {MachineGeneralInfo} from "../../../../model/custom/machine/machine-general-info";
import * as moment from "moment";
import {TimezoneService} from "../../../../services/timezone.service";
import {AlarmType} from "../../../../model/alarm-type";
import {Measure} from "../../../../model/measure";
import {AppConfig} from "../../../../shared/global-variables";
import {ExportFileType} from "../../../../model/export-file-type";


@Component({
  selector: 'app-machine-details-general',
  templateUrl: './machine-details-general.component.html',
  styleUrls: ['./machine-details-general.component.scss'],
})
export class MachineDetailsGeneralComponent implements OnInit {

  @ViewChild('squeezedValuesByHourOfDayDiv', {static: true}) squeezedValuesByHourOfDayDiv: ElementRef;
  @ViewChild('squeezedValuesMeasurementsDiv', {static: true}) squeezedValuesMeasurementsDiv: ElementRef;

  public readonly DEFAULT_ZOOM: number = 5;

  public machine: Machine
  public isMachineLoading: boolean = true;
  public isAlarmsLoading: boolean = true;
  public isGeneralInfoLoading: boolean = true;

  public language: string;

  public activeAlarms: AlarmTelemetryData[] = new Array(5);

  public squeezedValuesTranslation: string;
  public partialSqueezedValuesTranslation: string;
  public squeezedValuesByHourOfLastDayTranslation: string;
  public squeezedValuesLastDay: string;

  public machineWorkDailyCounter: string;

  public machineGeneralInfo: MachineGeneralInfo;
  public squeezedValuesByHourOfDay: { categories: string[], data: number[] };
  public squeezedValuesMeasurements: any;

  public alarmDistribution: [{ data: { name: string, y: number, color: string }[] }] = [{data: []}];
  public alarmDistributionCategories: string[] = [];

  public updateFlag: boolean = false;
  public squeezedValuesByHourOfDayDivWidth: number;
  public squeezedValuesMeasurementsDivWidth: number;

  public plotOptions: any;
  public pointFormatPieChart: string;

  public exportVisible: boolean;
  public viewInLiters: boolean = false;

  private readonly colors: string[] = [];

  private machineDetailsSubscription: Subscription;
  private updateSubscription: Subscription;

  private hoursTranslation: string;
  private minutesTranslation: string;
  private hoursOfDay: string[] = [];

  private map: mapboxgl.Map;


  constructor(
    private readonly mapService: MapService,
    private readonly machinesService: MachinesService,
    private readonly machineDetailsService: MachineDetailsService,
    private readonly i18n: TranslocoService,
    public readonly authGuard: AuthGuard,
    public readonly timezoneService: TimezoneService
  ) {
    this.hoursOfDay = this.timezoneService.getHoursOfDay();
    this.colors = AppConfig.COLORS;
  }

  ngOnInit(): void {
    this.updateSubscription = interval(60000).subscribe(
      () => {
        this.loadData();
      }
    );

    this.language = this.i18n.getActiveLang();
    this.setPlotOptions();
    this.loadTranslations();
    this.loadData();
  }

  private loadData(): void {
    this.isMachineLoading = true;
    this.isGeneralInfoLoading = true;
    this.isAlarmsLoading = true;

    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      if (machineDetails.machineLoading === false) {
        this.machine = machineDetails.machine;
        this.isMachineLoading = false;
        this.loadDetails();
      }
    });
  }

  private loadDetails(): void {
    this.loadMachineMap();
    this.loadGeneralInfo(this.machine);

    if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
      let tempActiveAlarms: AlarmTelemetryData[] = this.machine.telemetryData?.hasAlarms ? this.machine.telemetryData.activeAlarms : [];
      this.activeAlarms = tempActiveAlarms
        .filter(alarm => alarm.alarmType === AlarmType.ALARM)
        .sort((a, b) => new Date(a.activationDate) < new Date(b.activationDate) ? 1 : -1);
      this.isAlarmsLoading = false;
    }
  }

  private loadTranslations(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.hoursTranslation = this.i18n.translate('time.hours:short');
      this.minutesTranslation = this.i18n.translate('time.minutes:short');
      const hoursTranslation = this.i18n.translate('time.hours:short');
      this.hoursOfDay = this.hoursOfDay.map(h => h + hoursTranslation);
      this.pointFormatPieChart = this.i18n.translate('misc.alarms') + ': {point.y} ({point.percentage:.1f} %)<br>' + this.i18n.translate('attribute.total') + ': {point.total}';
      this.loadValuesTranslations();
    });
  }

  private loadValuesTranslations(): void {
    this.squeezedValuesTranslation = this.viewInLiters ?
      this.i18n.translate('misc.overview.squeezedLiters') :
      this.i18n.translate('misc.overview.squeezedFruits');
    this.partialSqueezedValuesTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.general.partialSqueezedLitersCounter') :
      this.i18n.translate('misc.info.general.partialSqueezedFruitsCounter');
    this.squeezedValuesByHourOfLastDayTranslation = this.viewInLiters ?
      this.i18n.translate('misc.info.general.squeezedLitersByHourOfLastDay') :
      this.i18n.translate('misc.info.general.squeezedFruitsByHourOfLastDay');
    this.squeezedValuesLastDay = this.viewInLiters ?
      this.i18n.translate('misc.info.general.squeezedLitersLastDay') :
      this.i18n.translate('misc.info.general.squeezedFruitsLastDay');
  }

  private loadMachineMap(): void {
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, this.machine.latitude, this.machine.longitude, 'map');
    this.mapService.buildStaticPulsingDot(this.map, this.machine.latitude, this.machine.longitude)
  }

  private loadGeneralInfo(machine: Machine): void {
    this.machinesService.getMachineGeneralInfo(machine.machineUuid, this.viewInLiters).subscribe(response => {
      this.machineGeneralInfo = response.machineGeneralInfo;

      this.machineWorkDailyCounter = response.machineGeneralInfo.machineWorkMinutesDaily != null ?
        this.timezoneService.minutesToTime(response.machineGeneralInfo.machineWorkMinutesDaily, this.hoursTranslation,
          this.minutesTranslation) : null;

      this.squeezedValuesByHourOfDay = this.parseMap(
        this.machineGeneralInfo?.squeezedValuesByHourOfDay,
        this.hoursOfDay,
        0
      );

      this.squeezedValuesMeasurements = this.machineGeneralInfo?.squeezedValuesMeasurements.map((measure: Measure) => {
        return [moment(measure.timestamp).utc().valueOf(), parseFloat(measure.value)]
      });

      this.alarmDistribution = [{
        data: Object.entries(this.machineGeneralInfo?.alarmDistribution).map((entry, index) => {
          return {name: entry[0], y: entry[1], color: this.colors[index % this.colors.length]}
        })
      }];
      this.alarmDistributionCategories = Object.keys(response.machineGeneralInfo.alarmDistribution);

      this.isGeneralInfoLoading = false;
    });
  }

  changeLiters(event: any): void {
    this.viewInLiters = event.checked;
    this.isGeneralInfoLoading = true;
    this.i18n.selectTranslation().subscribe(() => {
      this.loadValuesTranslations();
      this.loadGeneralInfo(this.machine);
    });
  }

  ngAfterViewInit(): void {
    this.squeezedValuesByHourOfDayDivWidth = this.squeezedValuesByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedValuesMeasurementsDivWidth = this.squeezedValuesMeasurementsDiv.nativeElement.offsetWidth;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this.squeezedValuesByHourOfDayDivWidth = this.squeezedValuesByHourOfDayDiv.nativeElement.offsetWidth;
    this.squeezedValuesMeasurementsDivWidth = this.squeezedValuesMeasurementsDiv.nativeElement.offsetWidth;
    this.updateFlag = true;
  }

  private parseMap(object: any, categories: string[], slicePositions: number = 0, initIndex: number = 0): {
    categories: string[],
    data: number[]
  } {
    if (object != null) {
      const map: Map<string, number> = new Map<string, number>(Object.entries(object));
      const size: number = map.size;

      if (size > 0) {
        return {
          categories: this.sliceArrayToLeft<string>(categories, slicePositions),
          data: this.sliceArrayToLeft<number>(this.getData(map, categories.length, initIndex), slicePositions)
        }
      }
    }

    return {
      categories: categories,
      data: null
    };
  }

  private sliceArrayToLeft<T>(array: T[], n: number): T[] {
    const position: number = n % array.length;
    return array.slice(position).concat(array.slice(0, position));
  }

  private getData(map: Map<string, number>, length: number, initIndex: number): number[] {
    const mapIterator = map.entries();
    const size = map.size;

    if (size > 0) {
      const array = Array.from({length}, (_, i) => 0);

      for (let i = 0; i < size; i++) {
        const value = mapIterator.next().value;
        array[value[0] - initIndex] = value[1];
      }
      return array;
    }
    return null;
  }

  private setPlotOptions(): void {
    this.plotOptions = {
      series: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color')
      }
    }
  }

  public tooltipFormatter(unit: string): string {
    const numberOfDecimals: number = this.viewInLiters ? 3 : 0;
    return `{point.y:,.${numberOfDecimals}f} ${unit}`;
  }

  public redirectToAlarms(): void {
    this.machineDetailsService.changeRoute('issues');
  }

  public getContentExport(): any[] {
    return [this.machine, this.machineGeneralInfo, this.activeAlarms, this.squeezedValuesByHourOfDay,
      this.squeezedValuesMeasurements, this.alarmDistribution[0], this.machineWorkDailyCounter, this.viewInLiters];
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
    if (this.updateSubscription != null) this.updateSubscription.unsubscribe();
  }

  public readonly ExportFileType = ExportFileType;
}
